import React, { useContext, useState, useEffect, useImperativeHandle, forwardRef, useRef, useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';
import AppContext from '../contexts/AppContext';

import Label from '../components/Label';
import Datepicker from '../components/Datepicker';
import ChipGroup from '../components/ChipGroup';
import EmailListManager from '../components/EmailListManager';
import PhoneListManager from '../components/PhoneListManager';
import UsergroupSelect from '../components/UsergroupSelect';
import { getUsergroups } from '../services/usergroupService';
import {
  CardContainer,
  TwoColumnLayout,
  SectionTitle as SharedSectionTitle,
  FormContent,
  StepContainer,
  Section,
  Input,
  InputWithIcon,
  Textarea,
  ErrorText,
  HelpText,
} from '../components/PrebookingSharedComponents';

import i18n from '../i18n';
import { isHeroBrandEnabled } from '../utils/heroBrandUtils';
import { getUsergroupReferenceDefaultFields } from '../utils/usergroupReferenceDefaults';
import useUsergroupReferenceDefaults from '../hooks/useUsergroupReferenceDefaults';
import { CalendarIcon, ExpandMoreIcon, ExpandLessIcon, DownloadIcon, CloseIcon } from '../components/PrebookingIcons';
import TruncatedFileName from '../components/TruncatedFileName';
import AttachmentPreview from '../components/AttachmentPreview';
import {
  ATTACHMENT_MAX_SIZE,
  ATTACHMENT_ALLOWED_TYPES,
  ATTACHMENT_ALLOWED_EXTENSIONS,
  getAllowedTypesDisplayString,
  ATTACHMENT_ACCEPT_STRING,
  EXTENSION_TO_MIME_MAP,
  isSignatureValid,
} from '../config/attachmentConfig';

// Collapsible section
const CollapsibleSection = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CollapsibleWrapper = styled.div`
  display: flex;
  gap: 32px;
  width: 100%;
`;

const EmptyTitle = styled.div`
  width: 200px;
  flex-shrink: 0;
`;

const CollapsibleContainer = styled.div`
  flex: 1;
  border: 1px solid rgba(69, 68, 107, 0.4);
  border-radius: 8px;
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  gap: 24px;

  &:hover {
    opacity: 0.8;
  }
`;

const ToggleTitle = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 8px 0;
`;

const ToggleText = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 1.4;
  color: #45446b;
  margin: 0;
  text-align: left;
`;

const ToggleActionButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  border: 1px solid rgba(69, 68, 107, 0.4);
  border-radius: 20px;
  background: white;

  p {
    font-family: 'Montserrat', sans-serif;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.15px;
    line-height: 1.2;
    color: #45446b;
    margin: 0;
  }

  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }
`;

const CollapsibleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: hidden;
  max-height: ${({ $isExpanded }) => ($isExpanded ? '500px' : '0')};
  opacity: ${({ $isExpanded }) => ($isExpanded ? '1' : '0')};
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
`;

const SearchContainer = styled.div`
  margin-top: 8px;
`;

// File upload components
const FileUploadButton = styled.label`
  /* Override global label styles */
  position: static !important;
  top: auto !important;
  margin-top: 0 !important;
  height: auto !important;
  line-height: 1.2 !important;
  color: #45446b !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 16px;
  border: 1px solid rgba(69, 68, 107, 0.4);
  background-color: white;
  border-radius: 20px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.15px;
  cursor: pointer;
  transition: all 0.2s;
  align-self: flex-start;
  max-width: 200px;

  &:hover {
    border-color: #45446b;
    background-color: rgba(69, 68, 107, 0.05);
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    transform: rotate(180deg);
  }

  input[type="file"] {
    display: none;
  }
`;

const FileInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  width: 100%;
  position: relative;
`;

const SelectedFileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: #f6f8fb;
  border: 1px solid rgba(69, 68, 107, 0.2);
  border-radius: 50px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.15px;
  color: #45446b;
  max-width: 100%;
  min-width: 0;
`;

const StyledTruncatedFileName = styled(TruncatedFileName)`
  overflow: hidden;
  min-width: 0;
`;

const RemoveFileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: rgba(69, 68, 107, 0.6);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #EA7E8F;

    svg path {
      stroke: #EA7E8F;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PrebookingInvoicingStep = forwardRef(({
  formData,
  updateField,
  updateAttachment,
  clearAttachment,
  addEmail,
  removeEmail,
  addPhone,
  removePhone,
  prebookingId,
  wizardMode,
  hasOriginalAttachment,
  preloadedUserGroups,
  preloadedUserGroupsLoading,
  preloadedIsUserGroupSearchAllowed,
}, ref) => {
  const { colors } = useContext(ThemeContext);
  const { sessionExpired } = useContext(FunctionsContext);
  const { user } = useContext(AppContext);
  const [errors, setErrors] = useState({});
  // Fallback-only local state: the wizard (step 1) prefetches user groups and passes
  // them via `preloadedUserGroups`, so the step normally renders with data available
  // immediately. We keep local state as a fallback for tests / direct mounts.
  const [localUserGroups, setLocalUserGroups] = useState([]);
  const [localLoading, setLocalLoading] = useState(!preloadedUserGroups);
  const [localIsSearchAllowed, setLocalIsSearchAllowed] = useState(false);
  const hasPreloaded = preloadedUserGroups !== undefined;
  const userGroups = hasPreloaded ? preloadedUserGroups : localUserGroups;
  const loading = hasPreloaded ? preloadedUserGroupsLoading : localLoading;
  const isSearchAllowed = hasPreloaded ? preloadedIsUserGroupSearchAllowed : localIsSearchAllowed;

  // Ref for attachment file input to allow reset
  const attachmentInputRef = useRef(null);
  const costAllocationContainerRef = useRef(null);

  // Raw search results, kept so a cost center picked through the "other" search can be
  // looked back up in full. `UsergroupSelect#onChange` hands back only
  // `{ id, name, customFields }` and is shared with the legacy booking flow, so its shape
  // cannot be widened here (TUL-5597).
  const searchedGroupsRef = useRef(new Map());

  // Prefill the reference inputs from the selected cost center (TUL-5597). Never in edit or
  // view mode: the value already stored on the prebooking wins over any cost center default.
  const referenceDefaultFields = useMemo(
    () => getUsergroupReferenceDefaultFields({ externalOrderIdUsed: user?.userGroup?.externalOrderIdUsed }),
    [user]
  );
  const referenceDefaultsEnabled = wizardMode !== 'edit' && wizardMode !== 'view';
  const applyReferenceDefaults = useUsergroupReferenceDefaults({
    updateField,
    enabled: referenceDefaultsEnabled,
    fields: referenceDefaultFields,
  });

  // Determine if this is an announcement (notice) booking
  const isAnnouncementBooking = formData.presence === 'notice';

  // Check if Cost Center and Invoice Ref are mandatory based on User Group settings
  // Invoice ref can also be mandatory on the selected cost allocation group (matching ChatWizard pattern)
  const selectedGroup = formData.costAllocation === 'other'
    ? formData.costAllocationGroup
    : userGroups.find(g => g.id === formData.costAllocation || g._id === formData.costAllocation);
  const isInvoiceRefMandatory = user?.userGroup?.invoiceRefMandatory || selectedGroup?.invoiceRefMandatory;
  const isCostAllocationMandatory = user?.userGroup?.invoiceRefUserGroupMandatory;
  const isAttachmentEnabled = true;

  // Fallback fetch when not preloaded (e.g. tests or direct mount of the step).
  // When the wizard preloads user groups in step 1, this effect is a no-op.
  useEffect(() => {
    if (hasPreloaded) return;
    const fetchUserGroups = async () => {
      setLocalLoading(true);
      try {
        const response = await getUsergroups(null, true);
        const groups = Array.isArray(response) ? response : response.groups || [];
        setLocalUserGroups(groups);
        if (!Array.isArray(response) && response.options) {
          setLocalIsSearchAllowed(!!response.options.allowSearch);
        }
      } catch (e) {
        console.error('Failed to fetch user groups:', e);
        if (e.message === 'Session expired') {
          sessionExpired();
        }
      } finally {
        setLocalLoading(false);
      }
    };

    fetchUserGroups();
  }, [sessionExpired, hasPreloaded, updateField]);

  // Auto-select a default group when loaded (works for both preloaded and local fetch):
  // for Hero brand users creating a new prebooking, the home usergroup (TUL-5514);
  // otherwise the single available group.
  useEffect(() => {
    if (loading) return;
    if (formData.costAllocation) {
      // A create flow can start with a cost center already chosen: `usePrebookingState`
      // seeds `costAllocation` from `user.invoiceUserGroup` without resolving the group, so
      // the auto-selection below never runs and that cost center's reference defaults would
      // never be applied. Resolve it here instead. Filling `costAllocationGroup` is what
      // keeps this from repeating, and unlike a ref it survives the wizard unmounting this
      // step while the customer visits another one (TUL-5597).
      if (!referenceDefaultsEnabled) return;
      if (formData.costAllocation === 'other' || formData.costAllocationGroup) return;
      // Match on `id` only, as with the home usergroup below.
      const initialGroup = userGroups.find(g => g.id === formData.costAllocation);
      if (!initialGroup) return;
      updateField('costAllocationGroup', {
        id: initialGroup.id,
        name: initialGroup.publicName || initialGroup.name,
        invoiceRecipientRef: initialGroup.invoiceRecipientRef,
        invoiceRefDefault: initialGroup.invoiceRefDefault,
        externalOrderIdDefault: initialGroup.externalOrderIdDefault,
      });
      applyReferenceDefaults({
        group: initialGroup,
        previousGroup: null,
        currentValues: {
          additionalInvoicingInfo: formData.additionalInvoicingInfo,
          externalOrderId: formData.externalOrderId,
        },
      });
      return;
    }
    let defaultGroup;
    if (isHeroBrandEnabled() && wizardMode !== 'edit') {
      // Match on `id` only: chip options and the saved selection are keyed by
      // `group.id`, so an `_id`-only match could not produce a valid selection.
      const homeGroupId = user?.userGroup?.id;
      defaultGroup = homeGroupId && userGroups.find(g => g.id === homeGroupId);
    }
    if (!defaultGroup && userGroups.length === 1) {
      defaultGroup = userGroups[0];
    }
    if (defaultGroup) {
      updateField('costAllocation', defaultGroup.id);
      updateField('costAllocationGroup', {
        id: defaultGroup.id,
        name: defaultGroup.publicName || defaultGroup.name,
        invoiceRecipientRef: defaultGroup.invoiceRecipientRef,
        invoiceRefDefault: defaultGroup.invoiceRefDefault,
        externalOrderIdDefault: defaultGroup.externalOrderIdDefault,
      });
      applyReferenceDefaults({
        group: defaultGroup,
        previousGroup: formData.costAllocationGroup,
        currentValues: {
          additionalInvoicingInfo: formData.additionalInvoicingInfo,
          externalOrderId: formData.externalOrderId,
        },
      });
    }
    // The reference values are listed only because exhaustive-deps requires it: every branch
    // above bails out once a group is resolved, so this cannot loop on its own prefill.
  }, [userGroups, loading, formData.costAllocation, formData.costAllocationGroup,
    formData.additionalInvoicingInfo, formData.externalOrderId, updateField,
    applyReferenceDefaults, referenceDefaultsEnabled, wizardMode, user]);

  // Search function for UsergroupSelect
  const searchUserGroups = async (query) => {
    try {
      const response = await getUsergroups(query);
      const groups = Array.isArray(response) ? response : response.groups || [];
      groups.forEach((group) => {
        if (group.id) { searchedGroupsRef.current.set(group.id, group); }
      });
      return groups;
    } catch (e) {
      if (e.message === 'Session expired') { sessionExpired(); }
      return [];
    }
  };

  // Build cost allocation options from user groups
  const costAllocationOptions = [
    ...userGroups.map(group => ({
      value: group.id,
      label: `${group.name}${group.invoiceRecipientRef ? ` (${group.invoiceRecipientRef})` : ''}`,
    })),
    ...(isSearchAllowed ? [{ value: 'other', label: i18n('other') }] : []),
  ];

  // Only show Cost Allocation when there are options or it's mandatory (TUL-4563)
  const shouldShowCostAllocation = loading || costAllocationOptions.length > 0 || isCostAllocationMandatory;

  const validateStep = () => {
    const newErrors = {};

    // Validate Cost Allocation if mandatory and section is visible
    if (shouldShowCostAllocation && isCostAllocationMandatory && !formData.costAllocation) {
      newErrors.costAllocation = i18n('required');
    }

    // Validate that "Other" has an actual group selected
    if (shouldShowCostAllocation && formData.costAllocation === 'other' && !formData.costAllocationGroup?.id) {
      newErrors.costAllocation = i18n('required');
    }

    // Validate Invoice Ref (Additional Invoicing Info) if mandatory
    if (isInvoiceRefMandatory && !formData.additionalInvoicingInfo?.trim()) {
      newErrors.additionalInvoicingInfo = i18n('required');
    }

    // External order id mandatory check (org-level toggle from user.userGroup)
    if (user?.userGroup?.externalOrderIdUsed && user?.userGroup?.externalOrderIdMandatory) {
      if (!formData.externalOrderId || formData.externalOrderId.trim().length === 0) {
        newErrors.externalOrderId = i18n('required');
      }
    }

    // Validate announcement-specific fields
    if (isAnnouncementBooking) {
      // Customer name and DOB only required for non-staging environments
      if (!isHeroBrandEnabled()) {
        if (!formData.customerName?.trim()) {
          newErrors.customerName = i18n('required');
        }
        if (!formData.dateOfBirth) {
          newErrors.dateOfBirth = i18n('required');
        }
      }
      if (!formData.mainPhoneNumber?.trim()) {
        newErrors.mainPhoneNumber = i18n('required');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Expose validation method to parent via ref
  useImperativeHandle(ref, () => ({
    validate: validateStep
  }));

  // Auto-detect edit flow: if costAllocation doesn't match any loaded group and search is allowed,
  // switch to 'other' to show search with pre-populated value
  useEffect(() => {
    if (loading || !isSearchAllowed) return;
    if (!formData.costAllocation || formData.costAllocation === 'other') return;
    const matchesLoadedGroup = userGroups.some(g => g.id === formData.costAllocation);
    if (!matchesLoadedGroup && formData.costAllocationGroup?.id) {
      updateField('costAllocation', 'other');
    }
  }, [loading, isSearchAllowed, userGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleCollapsible = () => {
    updateField('additionalInfoExpanded', !formData.additionalInfoExpanded);
  };


  const resetAttachmentInput = () => {
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const readFileSignatureHex = async (file, bytesToRead = 12) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const buffer = new Uint8Array(reader.result || []);
        const hexString = Array.from(buffer.slice(0, bytesToRead))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('');
        resolve(hexString.toLowerCase());
      };
      reader.onerror = () => reject(new Error('file_read_error'));
      reader.readAsArrayBuffer(file.slice(0, bytesToRead));
    });
  };

  const validateFileSignature = async (file, mimeType, extension) => {
    try {
      const signature = await readFileSignatureHex(file);
      return isSignatureValid(signature, mimeType, extension);
    } catch (signatureError) {
      console.error('Failed to validate attachment signature:', signatureError);
      return false;
    }
  };

  // Attachment upload handler (TUL-3848)
  const handleAttachmentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file is not empty
    if (file.size === 0) {
      setErrors({ ...errors, attachment: i18n('attachment_empty_file') || 'File is empty' });
      resetAttachmentInput();
      return;
    }

    const fileExtension = (file.name?.toLowerCase().match(/\.[0-9a-z]+$/) || [])[0] || '';
    const resolvedMimeType = ATTACHMENT_ALLOWED_TYPES[file.type] ? file.type : EXTENSION_TO_MIME_MAP[fileExtension];

    // Validate file extension (secondary security check)
    if (!ATTACHMENT_ALLOWED_EXTENSIONS.includes(fileExtension)) {
      const allowedTypesStr = getAllowedTypesDisplayString();
      setErrors({ ...errors, attachment: i18n('attachment_invalid_type') || `Invalid file type. Allowed: ${allowedTypesStr}` });
      resetAttachmentInput();
      return;
    }

    // Validate file type (MIME)
    if (!resolvedMimeType || !ATTACHMENT_ALLOWED_TYPES[resolvedMimeType]) {
      const allowedTypesStr = getAllowedTypesDisplayString();
      setErrors({ ...errors, attachment: i18n('attachment_invalid_type') || `Invalid file type. Allowed: ${allowedTypesStr}` });
      resetAttachmentInput();
      return;
    }

    // Validate file size
    if (file.size > ATTACHMENT_MAX_SIZE) {
      setErrors({ ...errors, attachment: i18n('attachment_file_too_large') || 'File must be less than 5MB' });
      resetAttachmentInput();
      return;
    }

    // Validate magic bytes / signature to reduce spoofing risk
    const signatureValid = await validateFileSignature(file, resolvedMimeType, fileExtension);
    if (!signatureValid) {
      const allowedTypesStr = getAllowedTypesDisplayString();
      setErrors({ ...errors, attachment: i18n('attachment_invalid_type') || `Invalid file type. Allowed: ${allowedTypesStr}` });
      resetAttachmentInput();
      return;
    }

    // Clear any previous error
    const newErrors = { ...errors };
    delete newErrors.attachment;
    setErrors(newErrors);

    const normalizedFile = file.type === resolvedMimeType || !resolvedMimeType
      ? file
      : new File([file], file.name, { type: resolvedMimeType });

    // Store file using the updateAttachment function
    updateAttachment(normalizedFile);
  };

  const handleRemoveAttachment = () => {
    clearAttachment();
    // Reset file input so the same file can be re-uploaded if needed
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
    // Clear any attachment-related errors
    const newErrors = { ...errors };
    delete newErrors.attachment;
    setErrors(newErrors);
  };

  return (
    <StepContainer colors={colors}>
      <CardContainer>
        <TwoColumnLayout>
          <SharedSectionTitle colors={colors}>{i18n('invoicing')}</SharedSectionTitle>
          <FormContent>
            {/* Cost allocation - hidden when no groups available and not mandatory (TUL-4563) */}
            {shouldShowCostAllocation && (
              <Section ref={costAllocationContainerRef}>
                <Label text={i18n('cost_allocation')} required={isCostAllocationMandatory} />
                {loading ? (
                  <div style={{ padding: '8px', color: 'rgba(69, 68, 107, 0.6)', fontSize: '14px' }}>
                    Loading...
                  </div>
                ) : (
                  <>
                    <ChipGroup
                      options={costAllocationOptions}
                      selectedValues={formData.costAllocation}
                      onChange={(value) => {
                        // Save the ID to costAllocation
                        updateField('costAllocation', value);

                        // Clear cost allocation error when user selects a value
                        if (value && errors.costAllocation) {
                          const newErrors = { ...errors };
                          delete newErrors.costAllocation;
                          setErrors(newErrors);
                        }

                        // Also save the complete userGroup object for easy access in summary
                        if (value === 'other') {
                          updateField('costAllocationGroup', null);
                          // Picking "other" deselects the cost center, so its default has to
                          // go with it. Left behind, it would look like a value the customer
                          // typed once a group is chosen from the search below (TUL-5597).
                          applyReferenceDefaults({
                            group: null,
                            previousGroup: formData.costAllocationGroup,
                            currentValues: {
                              additionalInvoicingInfo: formData.additionalInvoicingInfo,
                              externalOrderId: formData.externalOrderId,
                            },
                          });
                        } else {
                          const matchedGroup = userGroups.find(g => g.id === value || g._id === value);
                          if (matchedGroup) {
                            updateField('costAllocationGroup', {
                              id: matchedGroup.id || matchedGroup._id,
                              _id: matchedGroup._id || matchedGroup.id,
                              name: matchedGroup.publicName || matchedGroup.name,
                              invoiceRecipientRef: matchedGroup.invoiceRecipientRef,
                              invoiceRefDefault: matchedGroup.invoiceRefDefault,
                              externalOrderIdDefault: matchedGroup.externalOrderIdDefault
                            });
                            applyReferenceDefaults({
                              group: matchedGroup,
                              previousGroup: formData.costAllocationGroup,
                              currentValues: {
                                additionalInvoicingInfo: formData.additionalInvoicingInfo,
                                externalOrderId: formData.externalOrderId,
                              },
                            });
                          }
                        }
                      }}
                    />
                    {formData.costAllocation === 'other' && (
                      <SearchContainer>
                        {/* No `scrollContainerRef`: it snaps the search to the top of the page on every render (TUL-5750). */}
                        <UsergroupSelect
                          containerRef={costAllocationContainerRef}
                          selected={formData.costAllocationGroup}
                          onChange={(group) => {
                            // The full group comes from the search-result cache; a cache miss
                            // is treated as "no defaults", which clears a stale prefill
                            // instead of leaving the previous cost center's reference on screen.
                            const searchedGroup = searchedGroupsRef.current.get(group.id);
                            updateField('costAllocationGroup', {
                              ...group,
                              invoiceRefDefault: searchedGroup?.invoiceRefDefault,
                              externalOrderIdDefault: searchedGroup?.externalOrderIdDefault,
                            });
                            applyReferenceDefaults({
                              group: searchedGroup,
                              previousGroup: formData.costAllocationGroup,
                              currentValues: {
                                additionalInvoicingInfo: formData.additionalInvoicingInfo,
                                externalOrderId: formData.externalOrderId,
                              },
                            });
                            if (errors.costAllocation) {
                              const newErrors = { ...errors };
                              delete newErrors.costAllocation;
                              setErrors(newErrors);
                            }
                          }}
                          getSelectableItems={searchUserGroups}
                        />
                      </SearchContainer>
                    )}
                  </>
                )}
                {errors.costAllocation && <ErrorText colors={colors}>{errors.costAllocation}</ErrorText>}
              </Section>
            )}

            {/* VAT toggle - only shown when vatPolicy is 'select' */}
            {user?.userGroup?.vatPolicy === 'select' && (
              <Section>
                <Label text={i18n('prebooking_vat_question')} />
                <ChipGroup
                  options={[
                    { value: true, label: i18n('prebooking_vat_applied') },
                    { value: false, label: i18n('prebooking_vat_exempt') },
                  ]}
                  selectedValues={formData.vatIncluded}
                  onChange={(value) => updateField('vatIncluded', value)}
                />
              </Section>
            )}

            {/* External order ID - shown after VAT question per TUL-5433 */}
            {user?.userGroup?.externalOrderIdUsed && (
              <Section>
                <Label
                  text={user?.userGroup?.externalOrderIdTitle || i18n('external_order_id')}
                  required={!!user?.userGroup?.externalOrderIdMandatory}
                />
                <Input
                  colors={colors}
                  type="text"
                  value={formData.externalOrderId || ''}
                  onChange={(e) => {
                    updateField('externalOrderId', e.target.value);
                    if (e.target.value.trim() && errors.externalOrderId) {
                      const newErrors = { ...errors };
                      delete newErrors.externalOrderId;
                      setErrors(newErrors);
                    }
                  }}
                  placeholder={user?.userGroup?.externalOrderIdTitle || i18n('external_order_id')}
                />
                {errors.externalOrderId && <ErrorText colors={colors}>{errors.externalOrderId}</ErrorText>}
              </Section>
            )}

            {/* Additional invoicing information */}
            <Section>
              <Label text={user?.userGroup?.invoiceRefTitle || i18n('additional_invoicing_information')} required={isInvoiceRefMandatory} />
              <Input
                type="text"
                value={formData.additionalInvoicingInfo || ''}
                onChange={(e) => {
                  updateField('additionalInvoicingInfo', e.target.value);
                  // Clear error when user types
                  if (e.target.value && errors.additionalInvoicingInfo) {
                    const newErrors = { ...errors };
                    delete newErrors.additionalInvoicingInfo;
                    setErrors(newErrors);
                  }
                }}
                placeholder={i18n('field_not_visible_to_interpreter')}
              />
              {errors.additionalInvoicingInfo && <ErrorText colors={colors}>{errors.additionalInvoicingInfo}</ErrorText>}
            </Section>

            {/* Customer name - Only for announcement, hidden for staging */}
            {isAnnouncementBooking && !isHeroBrandEnabled() && (
              <Section>
                <Label text={i18n('customer_name_for_invoice')} />
                <Input
                  type="text"
                  value={formData.customerName || ''}
                  onChange={(e) => updateField('customerName', e.target.value)}
                  placeholder="Name Lastname"
                />
                {errors.customerName && <ErrorText colors={colors}>{errors.customerName}</ErrorText>}
              </Section>
            )}

            {/* Date of birth - Only for announcement, hidden for staging */}
            {isAnnouncementBooking && !isHeroBrandEnabled() && (
              <Section>
                <Label text={i18n('date_of_birth_for_invoice')} />
                <InputWithIcon>
                  <span className="input-icon"><CalendarIcon color={colors.iconAccentColor} size={20} /></span>
                  <Datepicker
                    selected={formData.dateOfBirth}
                    onChange={(date) => updateField('dateOfBirth', date)}
                    maxDate={new Date()}
                    placeholderText="DD.X.YYYY"
                    dateFormat="dd.M.yyyy"
                    customPadding="8px 8px 8px 40px"
                    customHeight="40px"
                    customBorder="1px solid rgba(0, 0, 0, 0.1)"
                  />
                </InputWithIcon>
                {errors.dateOfBirth && <ErrorText colors={colors}>{errors.dateOfBirth}</ErrorText>}
              </Section>
            )}

            {/* Recipients of confirmation and change notifications */}
            <Section>
              <Label text={i18n('recipients_of_confirmation_and_change_notifications')} />
              <EmailListManager
                emails={formData.ccEmails || []}
                onAdd={addEmail}
                onRemove={removeEmail}
                placeholder="name@mail.com"
                pinnedEmail={user?.email}
              />
              {errors.ccEmails && <ErrorText colors={colors}>{errors.ccEmails}</ErrorText>}
            </Section>
          </FormContent>
        </TwoColumnLayout>

        {/* Additional information for order handler - Collapsible */}
        <CollapsibleSection>
          <CollapsibleWrapper>
            <EmptyTitle />
            <CollapsibleContainer>
              <ToggleButton onClick={handleToggleCollapsible}>
                <ToggleTitle>
                  <ToggleText>
                    {i18n('additional_information_for_order_handler')}
                  </ToggleText>
                </ToggleTitle>
                <ToggleActionButton>
                  <p>{formData.additionalInfoExpanded ? i18n('hide') : i18n('open')}</p>
                  {formData.additionalInfoExpanded
                    ? <ExpandLessIcon color="#000000" size={24} />
                    : <ExpandMoreIcon color="#000000" size={24} />
                  }
                </ToggleActionButton>
              </ToggleButton>

              <CollapsibleContent $isExpanded={formData.additionalInfoExpanded}>
                {formData.additionalInfoExpanded && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Textarea
                      value={formData.additionalInfoForHandler || ''}
                      onChange={(e) => updateField('additionalInfoForHandler', e.target.value)}
                      placeholder=" "
                      rows={1}
                    />
                    <HelpText colors={colors}>
                      {i18n('note_filling_this_field_will_prolong_processing_time')}
                    </HelpText>
                  </div>
                )}
              </CollapsibleContent>
            </CollapsibleContainer>
          </CollapsibleWrapper>
        </CollapsibleSection>

      </CardContainer>


      {/* Information for interpreter */}
      <CardContainer>
        <TwoColumnLayout>
          <SharedSectionTitle colors={colors}>
            {i18n('information_for_interpreter')}
          </SharedSectionTitle>
          <FormContent>
            {isAnnouncementBooking ? (
              <>
                {/* Alternative contact person - for announcements only */}
                <Section>
                  <Label text={i18n('to_whom_message_forwarded_if_not_customer')} />
                  <Input
                    type="text"
                    value={formData.alternativeContactPerson || ''}
                    onChange={(e) => updateField('alternativeContactPerson', e.target.value)}
                    placeholder={i18n('write_here')}
                  />
                </Section>

                {/* Main phone number - for announcements only */}
                <Section>
                  <Label text={i18n('main_telephone_number_interpreter_will_call')} />
                  <Input
                    type="tel"
                    value={formData.mainPhoneNumber || ''}
                    onChange={(e) => updateField('mainPhoneNumber', e.target.value)}
                    placeholder="+358 40 xxx xxxx"
                  />
                  {errors.mainPhoneNumber && <ErrorText colors={colors}>{errors.mainPhoneNumber}</ErrorText>}
                </Section>

                {/* Additional phone numbers - for announcements only */}
                <Section>
                  <Label text={i18n('additional_telephone_numbers_interpreter_will_call')} />
                  <PhoneListManager
                    phoneNumbers={formData.additionalPhoneNumbers || []}
                    onAdd={addPhone}
                    onRemove={removePhone}
                    placeholder="+358 40 xxx xxxx"
                  />
                </Section>
              </>
            ) : (
              <>
                {/* Client name - Hidden for Hero brand (staging) as per Nav tender feedback
                   "Din klients navn som trenger tolken" - not a practice in Norway */}
                {!isHeroBrandEnabled() && (
                  <Section>
                    <Label text={i18n('patient_name')} />
                    <Input
                      type="text"
                      value={formData.clientName || ''}
                      onChange={(e) => updateField('clientName', e.target.value)}
                      placeholder={i18n('field_information_not_visible_on_bill')}
                    />
                  </Section>
                )}

                {/* Subject - for regular bookings */}
                <Section>
                  <Label text={i18n('subject')} />
                  <Input
                    type="text"
                    value={formData.subject || ''}
                    onChange={(e) => updateField('subject', e.target.value)}
                    placeholder={i18n('field_information_not_visible_on_bill')}
                  />
                </Section>

                {/* URL field - only for conference mode */}
                {formData.mode === 'conference' && (
                  <Section>
                    <Label text={i18n('url_for_video_meeting_label')} />
                    <Input
                      type="text"
                      value={formData.videoMeetingUrl || ''}
                      onChange={(e) => updateField('videoMeetingUrl', e.target.value)}
                      placeholder={i18n('url_for_video_meeting')}
                    />
                  </Section>
                )}

                {/* Reference document upload - uses presigned S3 URLs (TUL-3848) */}
                {isAttachmentEnabled && (wizardMode !== 'edit' || hasOriginalAttachment) && (
                  <Section>
                    <Label text={i18n('upload_reference_document_optional')} />
                    {wizardMode === 'edit' && prebookingId && !formData.attachment && (
                      <AttachmentPreview
                        prebookingId={prebookingId}
                        isCustomer={true}
                      />
                    )}
                    <FileInfoContainer>
                      <FileUploadButton>
                        <DownloadIcon color={colors.iconAccentColor} size={20} />
                        {i18n('browse_files')}
                        <input
                          ref={attachmentInputRef}
                          type="file"
                          accept={ATTACHMENT_ACCEPT_STRING}
                          onChange={handleAttachmentUpload}
                        />
                      </FileUploadButton>
                      {formData.attachment && (
                        <SelectedFileChip>
                          <StyledTruncatedFileName
                            filename={formData.attachment.filename}
                            fileSize={formData.attachment.size}
                          />
                          <RemoveFileButton
                            onClick={handleRemoveAttachment}
                            title={i18n('remove')}
                          >
                            <CloseIcon color="#45446b" size={16} />
                          </RemoveFileButton>
                        </SelectedFileChip>
                      )}
                      {errors.attachment && <ErrorText colors={colors}>{errors.attachment}</ErrorText>}
                      <HelpText colors={colors}>
                        {i18n('attachment_help_text') || 'Supported formats: PDF, DOCX, PPTX, TXT, PNG, JPG (max 5MB). Document will be visible to the assigned interpreter.'}
                      </HelpText>
                    </FileInfoContainer>
                  </Section>
                )}
              </>
            )}
          </FormContent>
        </TwoColumnLayout>
      </CardContainer>
    </StepContainer>
  );
});

PrebookingInvoicingStep.displayName = 'PrebookingInvoicingStep';

PrebookingInvoicingStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  updateAttachment: PropTypes.func.isRequired,
  clearAttachment: PropTypes.func.isRequired,
  addEmail: PropTypes.func.isRequired,
  removeEmail: PropTypes.func.isRequired,
  addPhone: PropTypes.func.isRequired,
  removePhone: PropTypes.func.isRequired,
};

export default PrebookingInvoicingStep;
